import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { getWsApiBaseUrl } from '../config/api';

const getUserIdFromToken = (token: string): string | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return payload.userId || payload.sub || null;
  } catch {
    return null;
  }
};

interface Room {
  roomId: string;
  userId: string;
  userName: string;
  name: string;
  profileImage: string;
  questionCount: number;
  totalMessages?: number;
  aiMessages?: number;
  lastMessage: string;
  lastMessageAt: string;
  isAiGenerated: boolean;
  status?: 'active' | 'resolved' | 'escalated' | 'archived';
  isEscalated?: boolean;
  isResolved?: boolean;
  avgResponseTimeMs?: number;
  totalTokensUsed?: number;
  topics?: string[];
  createdAt: string;
}

interface Message {
  messageId: string;
  roomId: string;
  senderId: string;
  senderName: string;
  senderUserName: string;
  message: string;
  messageType: string;
  isAiGenerated: boolean;
  aiData: {
    model?: string;
    tokensUsed?: number;
    latencyMs?: number;
    confidence?: number;
  } | null;
  createdAt: string;
  isSupport: boolean;
}

interface Stats {
  totalQuestions: number;
  totalResponses: number;
  totalConversations: number;
  todayQuestions: number;
  todayResponses: number;
  avgResponseTimeMs?: number;
  avgTokensPerResponse?: number;
  resolutionRate?: number;
  escalationRate?: number;
  topCategories?: { category: string; count: number }[];
  hourlyStats: { hour: number; label: string; questions: number; responses?: number }[];
  dailyStats: {
    date: string;
    label?: string;
    questions: number;
    conversations?: number;
    responses?: number;
    avgResponseTimeMs?: number;
  }[];
  recentQuestions: { message: string; roomId: string; createdAt: string; userId?: string }[];
}

type TabType = 'dashboard' | 'conversations' | 'analytics';
type RoomFilterType = 'all' | 'active' | 'resolved' | 'escalated';

const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatTimeAgo = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const formatDayLabel = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const safeNumber = (value?: number) => (typeof value === 'number' && !Number.isNaN(value) ? value : 0);

const StatusPill: React.FC<{ status: string; text: string }> = ({ status, text }) => {
  if (status === 'warning') {
    return <span className='rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700'>{text}</span>;
  }
  if (status === 'success') {
    return <span className='rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700'>{text}</span>;
  }
  if (status === 'danger') {
    return <span className='rounded-full bg-rose-100 px-2 py-1 text-xs font-semibold text-rose-700'>{text}</span>;
  }
  return <span className='rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700'>{text}</span>;
};

const AISupport: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [roomsPage, setRoomsPage] = useState(1);
  const [totalRooms, setTotalRooms] = useState(0);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [roomQuery, setRoomQuery] = useState('');
  const [roomFilter, setRoomFilter] = useState<RoomFilterType>('all');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const fetchRooms = useCallback(async (page: number = 1) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found');
      return;
    }

    const userId = getUserIdFromToken(token);
    if (!userId) {
      toast.error('Invalid token');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${getWsApiBaseUrl()}/support/t/dashboard?page=${page}&limit=20`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          accesstoken: token,
          userid: userId,
        },
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        toast.error(json.message || 'Failed to fetch rooms');
        return;
      }

      const fetchedRooms: Room[] = Array.isArray(json.data?.rooms) ? json.data.rooms : [];
      fetchedRooms.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());

      setRooms(fetchedRooms);
      setTotalRooms(safeNumber(json.data?.totalRooms));
      setRoomsPage(page);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch rooms');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (roomId: string, roomUserId?: string) => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const userId = getUserIdFromToken(token);
    if (!userId) return;

    setMessagesLoading(true);
    try {
      const res = await fetch(`${getWsApiBaseUrl()}/support/t/messages/${roomId}?limit=100`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          accesstoken: token,
          userid: userId,
        },
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        toast.error(json.message || 'Failed to fetch messages');
        return;
      }

      const fetchedMessages: Message[] = Array.isArray(json.data?.messages) ? json.data.messages : [];
      fetchedMessages.sort((a, b) => {
        const timeDiff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        if (timeDiff !== 0) return timeDiff;
        const aIsUser = roomUserId ? a.senderId === roomUserId : !a.isSupport;
        const bIsUser = roomUserId ? b.senderId === roomUserId : !b.isSupport;
        if (aIsUser === bIsUser) return 0;
        return aIsUser ? -1 : 1;
      });

      setMessages(fetchedMessages);
      requestAnimationFrame(() => scrollToBottom('auto'));
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch messages');
    } finally {
      setMessagesLoading(false);
    }
  }, [scrollToBottom]);

  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const userId = getUserIdFromToken(token);
    if (!userId) return;

    setStatsLoading(true);
    try {
      const res = await fetch(`${getWsApiBaseUrl()}/support/t/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          accesstoken: token,
          userid: userId,
        },
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        toast.error(json.message || 'Failed to fetch stats');
        return;
      }

      setStats(json.data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(() => {
    setLastRefresh(new Date());
    fetchRooms(roomsPage);
    fetchStats();
    if (selectedRoom) {
      fetchMessages(selectedRoom.roomId, selectedRoom.userId);
    }
    toast.success('Data refreshed');
  }, [fetchMessages, fetchRooms, fetchStats, roomsPage, selectedRoom]);

  useEffect(() => {
    fetchRooms(1);
    fetchStats();
  }, [fetchRooms, fetchStats]);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom('smooth');
    }
  }, [messages, scrollToBottom]);

  const handleRoomSelect = useCallback((room: Room) => {
    setSelectedRoom(room);
    fetchMessages(room.roomId, room.userId);
  }, [fetchMessages]);

  const filteredRooms = useMemo(() => {
    const normalizedQuery = roomQuery.trim().toLowerCase();
    return rooms.filter((room) => {
      const matchesQuery =
        normalizedQuery.length === 0 ||
        room.name?.toLowerCase().includes(normalizedQuery) ||
        room.userName?.toLowerCase().includes(normalizedQuery) ||
        room.lastMessage?.toLowerCase().includes(normalizedQuery);

      if (!matchesQuery) return false;
      if (roomFilter === 'all') return true;
      if (roomFilter === 'active') return !room.isEscalated && !room.isResolved;
      if (roomFilter === 'resolved') return !!room.isResolved;
      if (roomFilter === 'escalated') return !!room.isEscalated;
      return true;
    });
  }, [roomFilter, roomQuery, rooms]);

  const metrics = useMemo(() => {
    if (!stats) {
      return {
        responseRatePct: 0,
        backlogCount: 0,
        todayGap: 0,
        avgQuestionsPerConversation: 0,
        dailyAverageQuestions: 0,
      };
    }

    const totalQuestions = safeNumber(stats.totalQuestions);
    const totalResponses = safeNumber(stats.totalResponses);
    const totalConversations = safeNumber(stats.totalConversations);
    const daysWithData = Math.max(safeNumber(stats.dailyStats?.length), 1);

    return {
      responseRatePct: totalQuestions > 0 ? Math.round((totalResponses / totalQuestions) * 100) : 0,
      backlogCount: Math.max(totalQuestions - totalResponses, 0),
      todayGap: Math.max(safeNumber(stats.todayQuestions) - safeNumber(stats.todayResponses), 0),
      avgQuestionsPerConversation:
        totalConversations > 0 ? Number((totalQuestions / totalConversations).toFixed(2)) : 0,
      dailyAverageQuestions: Math.round(totalQuestions / daysWithData),
    };
  }, [stats]);

  const maxHourlyQuestions = useMemo(
    () => Math.max(...(stats?.hourlyStats?.map((item) => safeNumber(item.questions)) || [1]), 1),
    [stats]
  );
  const maxDailyQuestions = useMemo(
    () => Math.max(...(stats?.dailyStats?.map((item) => safeNumber(item.questions)) || [1]), 1),
    [stats]
  );
  const maxCategoryCount = useMemo(
    () => Math.max(...(stats?.topCategories?.map((item) => safeNumber(item.count)) || [1]), 1),
    [stats]
  );

  const responseHealthText =
    metrics.responseRatePct >= 95
      ? 'Healthy coverage'
      : metrics.responseRatePct >= 80
        ? 'Watch backlog'
        : 'Needs immediate attention';
  const responseHealthStatus =
    metrics.responseRatePct >= 95 ? 'success' : metrics.responseRatePct >= 80 ? 'warning' : 'danger';

  return (
    <div className='min-h-screen bg-slate-950 text-slate-100'>
      <Navbar />

      <div className='mx-auto w-full max-w-[1800px] px-4 py-6 sm:px-6 lg:px-8'>
        <header className='mb-6 rounded-2xl border border-slate-800 bg-slate-900/70 p-5 backdrop-blur'>
          <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
            <div>
              <h1 className='text-2xl font-semibold text-white sm:text-3xl'>AI Support Report</h1>
              <p className='mt-1 text-sm text-slate-400 sm:text-base'>
                Operational dashboard for conversation quality, response speed, and support load.
              </p>
            </div>
            <div className='flex flex-wrap items-center gap-3'>
              <span className='rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-400 sm:text-sm'>
                Last updated {lastRefresh.toLocaleTimeString()}
              </span>
              <button
                onClick={handleRefresh}
                disabled={loading || statsLoading || messagesLoading}
                className='rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60'
              >
                {loading || statsLoading || messagesLoading ? 'Refreshing...' : 'Refresh Data'}
              </button>
            </div>
          </div>
        </header>

        <div className='mb-6 flex flex-wrap gap-2'>
          {(['dashboard', 'conversations', 'analytics'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                activeTab === tab
                  ? 'bg-slate-100 text-slate-900'
                  : 'border border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-500 hover:text-white'
              }`}
            >
              {tab === 'dashboard' ? 'Overview' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'dashboard' && (
          <div className='space-y-6'>
            <section className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6'>
              <div className='rounded-xl border border-slate-800 bg-slate-900 p-4'>
                <p className='text-xs uppercase tracking-wide text-slate-400'>Conversations</p>
                <p className='mt-2 text-3xl font-semibold text-white'>{safeNumber(stats?.totalConversations)}</p>
                <p className='mt-1 text-xs text-slate-500'>Active support threads</p>
              </div>
              <div className='rounded-xl border border-slate-800 bg-slate-900 p-4'>
                <p className='text-xs uppercase tracking-wide text-slate-400'>Questions</p>
                <p className='mt-2 text-3xl font-semibold text-white'>{safeNumber(stats?.totalQuestions)}</p>
                <p className='mt-1 text-xs text-slate-500'>All user text requests</p>
              </div>
              <div className='rounded-xl border border-slate-800 bg-slate-900 p-4'>
                <p className='text-xs uppercase tracking-wide text-slate-400'>Responses</p>
                <p className='mt-2 text-3xl font-semibold text-white'>{safeNumber(stats?.totalResponses)}</p>
                <p className='mt-1 text-xs text-slate-500'>AI messages sent</p>
              </div>
              <div className='rounded-xl border border-slate-800 bg-slate-900 p-4'>
                <p className='text-xs uppercase tracking-wide text-slate-400'>Coverage</p>
                <p className='mt-2 text-3xl font-semibold text-white'>{metrics.responseRatePct}%</p>
                <p className='mt-1 text-xs text-slate-500'>Questions with AI answer</p>
              </div>
              <div className='rounded-xl border border-slate-800 bg-slate-900 p-4'>
                <p className='text-xs uppercase tracking-wide text-slate-400'>Avg Latency</p>
                <p className='mt-2 text-3xl font-semibold text-white'>
                  {stats?.avgResponseTimeMs ? `${Math.round(stats.avgResponseTimeMs)}ms` : 'N/A'}
                </p>
                <p className='mt-1 text-xs text-slate-500'>Mean AI generation time</p>
              </div>
              <div className='rounded-xl border border-slate-800 bg-slate-900 p-4'>
                <p className='text-xs uppercase tracking-wide text-slate-400'>Backlog</p>
                <p className='mt-2 text-3xl font-semibold text-white'>{metrics.backlogCount}</p>
                <p className='mt-1 text-xs text-slate-500'>Questions waiting for response</p>
              </div>
            </section>

            <section className='grid grid-cols-1 gap-6 xl:grid-cols-[2fr,1fr]'>
              <div className='rounded-2xl border border-slate-800 bg-slate-900 p-5'>
                <div className='mb-4 flex items-center justify-between'>
                  <h2 className='text-lg font-semibold text-white'>Operational Trend (Last 7 Days)</h2>
                  <StatusPill status={responseHealthStatus} text={responseHealthText} />
                </div>
                {statsLoading ? (
                  <div className='flex h-48 items-center justify-center text-slate-400'>Loading daily trend...</div>
                ) : (
                  <div className='space-y-4'>
                    {stats?.dailyStats?.map((day) => {
                      const questions = safeNumber(day.questions);
                      const responses = safeNumber(day.responses);
                      const answerRate = questions > 0 ? Math.round((responses / questions) * 100) : 0;
                      return (
                        <div key={day.date} className='grid grid-cols-[120px,1fr,80px] items-center gap-3'>
                          <span className='text-sm text-slate-400'>{day.label || day.date}</span>
                          <div className='h-3 overflow-hidden rounded-full bg-slate-800'>
                            <div
                              className='h-full rounded-full bg-slate-300'
                              style={{ width: `${(questions / maxDailyQuestions) * 100}%` }}
                            />
                          </div>
                          <span className='text-right text-sm text-slate-300'>
                            {questions} Q / {answerRate}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className='rounded-2xl border border-slate-800 bg-slate-900 p-5'>
                <h2 className='text-lg font-semibold text-white'>Action Focus</h2>
                <div className='mt-4 space-y-3 text-sm text-slate-300'>
                  <div className='rounded-lg border border-slate-800 bg-slate-950/70 p-3'>
                    <p className='font-medium text-white'>Coverage</p>
                    <p className='mt-1 text-slate-400'>
                      Keep response coverage above 95%. Current value is {metrics.responseRatePct}%.
                    </p>
                  </div>
                  <div className='rounded-lg border border-slate-800 bg-slate-950/70 p-3'>
                    <p className='font-medium text-white'>Backlog</p>
                    <p className='mt-1 text-slate-400'>
                      Backlog is {metrics.backlogCount}. Track this after each deployment or prompt update.
                    </p>
                  </div>
                  <div className='rounded-lg border border-slate-800 bg-slate-950/70 p-3'>
                    <p className='font-medium text-white'>Today Gap</p>
                    <p className='mt-1 text-slate-400'>
                      Today unresolved volume is {metrics.todayGap}. Use it as the live pressure indicator.
                    </p>
                  </div>
                  <div className='rounded-lg border border-slate-800 bg-slate-950/70 p-3'>
                    <p className='font-medium text-white'>Conversation Density</p>
                    <p className='mt-1 text-slate-400'>
                      Average questions per conversation: {metrics.avgQuestionsPerConversation}. Rising values can indicate
                      unclear responses.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className='grid grid-cols-1 gap-6 xl:grid-cols-[2fr,1fr]'>
              <div className='rounded-2xl border border-slate-800 bg-slate-900 p-5'>
                <h2 className='text-lg font-semibold text-white'>Hourly Load (Today)</h2>
                {statsLoading ? (
                  <div className='flex h-44 items-center justify-center text-slate-400'>Loading hourly chart...</div>
                ) : (
                  <div className='mt-4 overflow-x-auto'>
                    <div className='flex min-w-[680px] items-end gap-1'>
                      {stats?.hourlyStats?.map((hour) => (
                        <div key={hour.hour} className='flex w-full flex-col items-center'>
                          <div
                            className='w-full rounded-t bg-slate-300/90'
                            style={{ height: `${Math.max((safeNumber(hour.questions) / maxHourlyQuestions) * 130, 4)}px` }}
                          />
                          {hour.hour % 3 === 0 && <span className='mt-2 text-[11px] text-slate-500'>{hour.label}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className='rounded-2xl border border-slate-800 bg-slate-900 p-5'>
                <h2 className='text-lg font-semibold text-white'>Recent Questions</h2>
                <div className='mt-4 max-h-56 space-y-2 overflow-y-auto pr-1'>
                  {stats?.recentQuestions?.length ? (
                    stats.recentQuestions.slice(0, 8).map((item, idx) => (
                      <button
                        key={`${item.roomId}-${idx}`}
                        onClick={() => {
                          const room = rooms.find((entry) => entry.roomId === item.roomId);
                          if (room) {
                            setActiveTab('conversations');
                            handleRoomSelect(room);
                          }
                        }}
                        className='w-full rounded-lg border border-slate-800 bg-slate-950 p-3 text-left transition hover:border-slate-600'
                      >
                        <p className='truncate text-sm text-slate-200'>{item.message}</p>
                        <p className='mt-1 text-xs text-slate-500'>{formatTimeAgo(item.createdAt)}</p>
                      </button>
                    ))
                  ) : (
                    <p className='text-sm text-slate-500'>No recent questions available.</p>
                  )}
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'conversations' && (
          <div className='grid min-h-[680px] grid-cols-1 gap-5 xl:grid-cols-[360px,1fr]'>
            <section className='flex h-full flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-900'>
              <div className='space-y-3 border-b border-slate-800 p-4'>
                <h2 className='text-lg font-semibold text-white'>Conversations</h2>
                <div className='grid grid-cols-1 gap-2'>
                  <input
                    value={roomQuery}
                    onChange={(event) => setRoomQuery(event.target.value)}
                    placeholder='Search by name, username, or last message'
                    className='w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200 outline-none ring-0 placeholder:text-slate-500 focus:border-slate-500'
                  />
                  <div className='flex gap-2'>
                    {(['all', 'active', 'resolved', 'escalated'] as RoomFilterType[]).map((filter) => (
                      <button
                        key={filter}
                        onClick={() => setRoomFilter(filter)}
                        className={`rounded-md px-3 py-1 text-xs font-medium ${
                          roomFilter === filter
                            ? 'bg-slate-100 text-slate-900'
                            : 'border border-slate-700 bg-slate-950 text-slate-400 hover:border-slate-500'
                        }`}
                      >
                        {filter.charAt(0).toUpperCase() + filter.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className='text-xs text-slate-500'>
                  Showing {filteredRooms.length} of {totalRooms} conversations
                </div>
              </div>

              <div className='flex-1 overflow-y-auto'>
                {loading ? (
                  <div className='flex h-32 items-center justify-center text-sm text-slate-400'>Loading conversations...</div>
                ) : filteredRooms.length ? (
                  filteredRooms.map((room) => (
                    <button
                      key={room.roomId}
                      onClick={() => handleRoomSelect(room)}
                      className={`w-full border-b border-slate-800 px-4 py-4 text-left transition hover:bg-slate-800/60 ${
                        selectedRoom?.roomId === room.roomId ? 'bg-slate-800/80' : ''
                      }`}
                    >
                      <div className='flex items-start justify-between gap-2'>
                        <div className='min-w-0'>
                          <p className='truncate text-sm font-semibold text-white'>{room.name || 'Unknown user'}</p>
                          <p className='truncate text-xs text-slate-500'>@{room.userName || 'unknown'}</p>
                        </div>
                        <span className='text-xs text-slate-500'>{formatTimeAgo(room.lastMessageAt)}</span>
                      </div>

                      <div className='mt-2 flex items-center gap-2'>
                        {room.isEscalated && <StatusPill status='warning' text='Escalated' />}
                        {room.isResolved && <StatusPill status='success' text='Resolved' />}
                        {!room.isEscalated && !room.isResolved && <StatusPill status='neutral' text='Active' />}
                      </div>

                      <p className='mt-2 truncate text-xs text-slate-400'>{room.lastMessage || 'No last message'}</p>
                      <p className='mt-2 text-xs text-slate-500'>
                        Questions: {room.questionCount || 0}
                        {safeNumber(room.avgResponseTimeMs) > 0 && ` | Avg ${Math.round(safeNumber(room.avgResponseTimeMs))}ms`}
                      </p>
                    </button>
                  ))
                ) : (
                  <div className='flex h-32 items-center justify-center text-sm text-slate-500'>No matching conversations.</div>
                )}
              </div>

              {totalRooms > 20 && (
                <div className='flex items-center justify-between border-t border-slate-800 p-3 text-sm'>
                  <button
                    onClick={() => fetchRooms(roomsPage - 1)}
                    disabled={roomsPage === 1}
                    className='rounded-md border border-slate-700 px-3 py-1 text-slate-300 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    Prev
                  </button>
                  <span className='text-slate-400'>
                    Page {roomsPage} / {Math.ceil(totalRooms / 20)}
                  </span>
                  <button
                    onClick={() => fetchRooms(roomsPage + 1)}
                    disabled={roomsPage * 20 >= totalRooms}
                    className='rounded-md border border-slate-700 px-3 py-1 text-slate-300 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    Next
                  </button>
                </div>
              )}
            </section>

            <section className='flex h-full flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-900'>
              {selectedRoom ? (
                <>
                  <div className='border-b border-slate-800 px-5 py-4'>
                    <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
                      <div>
                        <p className='text-lg font-semibold text-white'>{selectedRoom.name || 'Unknown user'}</p>
                        <p className='text-sm text-slate-500'>@{selectedRoom.userName || 'unknown'}</p>
                      </div>
                      <div className='flex flex-wrap items-center gap-2 text-xs text-slate-400'>
                        <span className='rounded-md border border-slate-700 px-2 py-1'>
                          Questions {selectedRoom.questionCount || 0}
                        </span>
                        <span className='rounded-md border border-slate-700 px-2 py-1'>
                          Last {formatDateTime(selectedRoom.lastMessageAt)}
                        </span>
                        <button
                          onClick={() => fetchMessages(selectedRoom.roomId, selectedRoom.userId)}
                          className='rounded-md bg-slate-100 px-3 py-1 font-semibold text-slate-900'
                        >
                          Reload
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className='flex-1 overflow-y-auto bg-slate-950/60 px-4 py-5 sm:px-6'>
                    {messagesLoading ? (
                      <div className='flex h-full items-center justify-center text-sm text-slate-400'>Loading messages...</div>
                    ) : messages.length > 0 ? (
                      <div className='space-y-3'>
                        {(() => {
                          let previousDay = '';
                          return messages.map((msg) => {
                            const currentDay = formatDayLabel(msg.createdAt);
                            const showDayMarker = previousDay !== currentDay;
                            previousDay = currentDay;

                            const isUserMessage = selectedRoom.userId
                              ? msg.senderId === selectedRoom.userId
                              : !msg.isSupport;

                            return (
                              <React.Fragment key={msg.messageId}>
                                {showDayMarker && (
                                  <div className='my-4 flex items-center justify-center'>
                                    <span className='rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-xs text-slate-400'>
                                      {currentDay}
                                    </span>
                                  </div>
                                )}
                                <div className={`flex ${isUserMessage ? 'justify-end' : 'justify-start'}`}>
                                  <div
                                    className={`max-w-[88%] rounded-2xl px-4 py-3 shadow-sm sm:max-w-[75%] ${
                                      isUserMessage
                                        ? 'rounded-tr-sm bg-slate-100 text-slate-900'
                                        : 'rounded-tl-sm border border-slate-700 bg-slate-900 text-slate-100'
                                    }`}
                                  >
                                    <div className='mb-1 flex items-center justify-between gap-3'>
                                      <span
                                        className={`text-xs font-semibold ${
                                          isUserMessage ? 'text-slate-700' : 'text-slate-400'
                                        }`}
                                      >
                                        {isUserMessage ? (msg.senderName || msg.senderUserName || 'User') : 'PingTop AI Support'}
                                      </span>
                                      <span className={`text-[11px] ${isUserMessage ? 'text-slate-500' : 'text-slate-500'}`}>
                                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                    <p className='whitespace-pre-wrap break-words text-sm leading-relaxed sm:text-[15px]'>
                                      {msg.message}
                                    </p>
                                    {!isUserMessage && msg.isAiGenerated && (
                                      <div className='mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500'>
                                        {msg.aiData?.model && <span className='rounded bg-slate-800 px-2 py-1'>{msg.aiData.model}</span>}
                                        {safeNumber(msg.aiData?.latencyMs) > 0 && (
                                          <span className='rounded bg-slate-800 px-2 py-1'>{Math.round(safeNumber(msg.aiData?.latencyMs))}ms</span>
                                        )}
                                        {safeNumber(msg.aiData?.tokensUsed) > 0 && (
                                          <span className='rounded bg-slate-800 px-2 py-1'>{Math.round(safeNumber(msg.aiData?.tokensUsed))} tokens</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </React.Fragment>
                            );
                          });
                        })()}
                        <div ref={messagesEndRef} />
                      </div>
                    ) : (
                      <div className='flex h-full items-center justify-center text-sm text-slate-500'>No messages in this conversation.</div>
                    )}
                  </div>
                </>
              ) : (
                <div className='flex h-full items-center justify-center px-6 text-center'>
                  <div>
                    <p className='text-lg font-medium text-slate-200'>Select a conversation to inspect chat quality</p>
                    <p className='mt-2 text-sm text-slate-500'>
                      The timeline is rendered in chronological order with newest messages at the bottom.
                    </p>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className='space-y-6'>
            {statsLoading ? (
              <div className='flex min-h-[360px] items-center justify-center rounded-2xl border border-slate-800 bg-slate-900 text-slate-400'>
                Loading analytics...
              </div>
            ) : stats ? (
              <>
                <section className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6'>
                  <div className='rounded-xl border border-slate-800 bg-slate-900 p-4'>
                    <p className='text-xs uppercase text-slate-400'>Avg Questions/Day</p>
                    <p className='mt-2 text-2xl font-semibold text-white'>{metrics.dailyAverageQuestions}</p>
                  </div>
                  <div className='rounded-xl border border-slate-800 bg-slate-900 p-4'>
                    <p className='text-xs uppercase text-slate-400'>Response Rate</p>
                    <p className='mt-2 text-2xl font-semibold text-white'>{metrics.responseRatePct}%</p>
                  </div>
                  <div className='rounded-xl border border-slate-800 bg-slate-900 p-4'>
                    <p className='text-xs uppercase text-slate-400'>Avg Response Time</p>
                    <p className='mt-2 text-2xl font-semibold text-white'>
                      {stats.avgResponseTimeMs ? `${Math.round(stats.avgResponseTimeMs)}ms` : 'N/A'}
                    </p>
                  </div>
                  <div className='rounded-xl border border-slate-800 bg-slate-900 p-4'>
                    <p className='text-xs uppercase text-slate-400'>Avg Tokens/Response</p>
                    <p className='mt-2 text-2xl font-semibold text-white'>
                      {stats.avgTokensPerResponse ? Math.round(stats.avgTokensPerResponse) : 'N/A'}
                    </p>
                  </div>
                  <div className='rounded-xl border border-slate-800 bg-slate-900 p-4'>
                    <p className='text-xs uppercase text-slate-400'>Resolution Rate</p>
                    <p className='mt-2 text-2xl font-semibold text-white'>
                      {stats.resolutionRate !== undefined ? `${Math.round(stats.resolutionRate * 100)}%` : 'N/A'}
                    </p>
                  </div>
                  <div className='rounded-xl border border-slate-800 bg-slate-900 p-4'>
                    <p className='text-xs uppercase text-slate-400'>Escalation Rate</p>
                    <p className='mt-2 text-2xl font-semibold text-white'>
                      {stats.escalationRate !== undefined ? `${Math.round(stats.escalationRate * 100)}%` : 'N/A'}
                    </p>
                  </div>
                </section>

                <section className='grid grid-cols-1 gap-6 xl:grid-cols-[2fr,1fr]'>
                  <div className='rounded-2xl border border-slate-800 bg-slate-900 p-5'>
                    <h2 className='text-lg font-semibold text-white'>Daily Breakdown</h2>
                    <div className='mt-4 overflow-x-auto'>
                      <table className='w-full min-w-[760px] table-fixed border-collapse text-sm'>
                        <thead>
                          <tr className='border-b border-slate-800 text-left text-slate-400'>
                            <th className='py-2 font-medium'>Date</th>
                            <th className='py-2 font-medium'>Questions</th>
                            <th className='py-2 font-medium'>Responses</th>
                            <th className='py-2 font-medium'>Answer Rate</th>
                            <th className='py-2 font-medium'>Conversations</th>
                            <th className='py-2 font-medium'>Avg Latency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.dailyStats.map((day) => {
                            const questions = safeNumber(day.questions);
                            const responses = safeNumber(day.responses);
                            const rate = questions > 0 ? Math.round((responses / questions) * 100) : 0;
                            return (
                              <tr key={day.date} className='border-b border-slate-800/60 text-slate-300'>
                                <td className='py-2'>{day.label || day.date}</td>
                                <td className='py-2'>{questions}</td>
                                <td className='py-2'>{responses}</td>
                                <td className='py-2'>{rate}%</td>
                                <td className='py-2'>{safeNumber(day.conversations)}</td>
                                <td className='py-2'>
                                  {safeNumber(day.avgResponseTimeMs) > 0 ? `${Math.round(safeNumber(day.avgResponseTimeMs))}ms` : 'N/A'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className='rounded-2xl border border-slate-800 bg-slate-900 p-5'>
                    <h2 className='text-lg font-semibold text-white'>How to Analyze</h2>
                    <div className='mt-4 space-y-3 text-sm text-slate-300'>
                      <div className='rounded-lg border border-slate-800 bg-slate-950 p-3'>
                        <p className='font-medium text-white'>1. Start with coverage</p>
                        <p className='mt-1 text-slate-400'>Coverage under 90% means users are not consistently getting answers.</p>
                      </div>
                      <div className='rounded-lg border border-slate-800 bg-slate-950 p-3'>
                        <p className='font-medium text-white'>2. Check latency spikes</p>
                        <p className='mt-1 text-slate-400'>Latency growth usually points to model or retrieval overhead in production.</p>
                      </div>
                      <div className='rounded-lg border border-slate-800 bg-slate-950 p-3'>
                        <p className='font-medium text-white'>3. Watch escalation trend</p>
                        <p className='mt-1 text-slate-400'>Rising escalations indicate prompt gaps or policy coverage issues.</p>
                      </div>
                      <div className='rounded-lg border border-slate-800 bg-slate-950 p-3'>
                        <p className='font-medium text-white'>4. Track category concentration</p>
                        <p className='mt-1 text-slate-400'>If one category dominates, target docs and prompt examples there first.</p>
                      </div>
                    </div>
                  </div>
                </section>

                <section className='grid grid-cols-1 gap-6 xl:grid-cols-2'>
                  <div className='rounded-2xl border border-slate-800 bg-slate-900 p-5'>
                    <h2 className='text-lg font-semibold text-white'>Hourly Questions</h2>
                    <div className='mt-4 overflow-x-auto'>
                      <div className='flex min-w-[680px] items-end gap-1'>
                        {stats.hourlyStats.map((hour) => (
                          <div key={hour.hour} className='flex w-full flex-col items-center'>
                            <div
                              className='w-full rounded-t bg-slate-300'
                              style={{ height: `${Math.max((safeNumber(hour.questions) / maxHourlyQuestions) * 130, 4)}px` }}
                            />
                            {hour.hour % 3 === 0 && <span className='mt-2 text-[11px] text-slate-500'>{hour.label}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className='rounded-2xl border border-slate-800 bg-slate-900 p-5'>
                    <h2 className='text-lg font-semibold text-white'>Top Categories</h2>
                    <div className='mt-4 space-y-3'>
                      {stats.topCategories && stats.topCategories.length > 0 ? (
                        stats.topCategories.slice(0, 10).map((category) => (
                          <div key={category.category || 'general'} className='space-y-1'>
                            <div className='flex items-center justify-between text-sm text-slate-300'>
                              <span className='truncate'>{category.category || 'General'}</span>
                              <span>{category.count}</span>
                            </div>
                            <div className='h-2 overflow-hidden rounded-full bg-slate-800'>
                              <div
                                className='h-full rounded-full bg-slate-300'
                                style={{ width: `${(safeNumber(category.count) / maxCategoryCount) * 100}%` }}
                              />
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className='text-sm text-slate-500'>No category data available yet.</p>
                      )}
                    </div>
                  </div>
                </section>
              </>
            ) : (
              <div className='rounded-2xl border border-slate-800 bg-slate-900 p-8 text-center text-slate-400'>
                No analytics data available yet.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AISupport;

